import React from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getProductName, getProductDescription } from '../../utils/productUtils';

import { Product } from '../../types/product';

interface CartConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  product: Product | null;
}

const CartConfirmationDialog: React.FC<CartConfirmationDialogProps> = ({
  open,
  onClose,
  product,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  if (!product) return null;

  const handleViewCart = () => {
    onClose();
    navigate('/cart');
  };

  const handleContinueShopping = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxWidth: 500,
        },
      }}
    >
      <DialogContent sx={{ p: 0, position: 'relative' }}>
        {/* Close button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            zIndex: 1,
            color: '#666',
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Header */}
        <Box sx={{ p: 3, pb: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: '20px',
              color: '#17110D',
              mb: 3,
            }}
          >
            {t('cartConfirmation.addedToCart')}
          </Typography>

          {/* Product details */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            {/* Product image */}
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: 1,
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              <img
                src={product.imageUrl || '/images/img_placeholder.svg'}
                alt={getProductName(product, i18n.language)}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/images/img_placeholder.svg';
                }}
              />
            </Box>

            {/* Product info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Brand */}
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '14px',
                  color: '#17110D',
                  mb: 0.5,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                {product.brand || product.vendor || t('cartConfirmation.brand')}
              </Typography>

              {/* Product name */}
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: '14px',
                  color: '#17110D',
                  mb: 0.5,
                  lineHeight: 1.3,
                }}
                noWrap
              >
                {getProductName(product as any, i18n.language)}
              </Typography>

              {/* Product description/details */}
              {product.description && (
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: '#666',
                    mb: 0.5,
                    lineHeight: 1.3,
                    maxHeight: '5.6em', // Limit to 2 lines (2 * 1.3em)
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    textOverflow: 'ellipsis',
                    '& img': {
                      maxWidth: '100%',
                      height: 'auto',
                      display: 'block',
                      margin: '4px 0',
                    },
                    '& p': {
                      margin: '4px 0',
                    },
                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                      margin: '6px 0 4px 0',
                      fontWeight: 400,
                      fontSize: '14px',
                    },
                    '& ul, & ol': {
                      margin: '4px 0',
                      paddingLeft: '16px',
                    },
                    '& li': {
                      margin: '2px 0',
                    },
                  }}
                  dangerouslySetInnerHTML={{ __html: getProductDescription(product as any, i18n.language) || '' }}
                />
              )}

              {/* Quantity */}
              <Typography
                sx={{
                  fontSize: '14px',
                  mb: 0.5,
                }}
              >
                {t('cartConfirmation.quantity')} {product.quantity}
              </Typography>

              {/* Price */}
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '16px',
                  color: '#17110D',
                }}
              >
                HK${product.price.toFixed(2)}
              </Typography>
            </Box>
          </Box>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleViewCart}
              sx={{
                flex: 1,
                borderColor: '#17110D',
                color: '#17110D',
                fontWeight: 600,
                py: 1.5,
                '&:hover': {
                  borderColor: '#17110D',
                  backgroundColor: 'rgba(23, 17, 13, 0.04)',
                },
              }}
            >
              {t('cartConfirmation.viewCart')}
            </Button>
            <Button
              variant="contained"
              onClick={handleContinueShopping}
              sx={{
                flex: 1,
                backgroundColor: '#17110D',
                color: '#fff',
                fontWeight: 600,
                py: 1.5,
                '&:hover': {
                  backgroundColor: '#000',
                },
              }}
            >
              {t('cartConfirmation.continueShopping')}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CartConfirmationDialog;
